import React, {Fragment, useState} from 'react';
import {Button, Form, Modal} from 'react-bootstrap';
import {Formik} from 'formik';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {showPlayersModal, hidePlayersModal, searchPlayers, hideEditPlayersModal, cleanPlayersReceived, saveEditedPlayers} from '../actions/player';
import { showMessage } from '../actions/messages';
import {BookingPlayerSearch} from './booking-player-search';

const isParticipantInvite = inviteType => inviteType === 'partecipant' || Number(inviteType) === 1;
const isGuestInvite = inviteType => inviteType === 'guest' || Number(inviteType) === 2;

export const editableInvitationRoster = (booking, organizerUid) => {
    const invitedPlayers = [];
    const invitedGuests = [];
    (booking && Array.isArray(booking.users) ? booking.users : []).forEach(userBooking => {
        if (isParticipantInvite(userBooking.inviteType)) {
            invitedPlayers.push({
                uid: userBooking.uid,
                name: userBooking.partnerBookingName || '',
                surname: userBooking.partnerBookingSurname || '',
                nickname: userBooking.bookingOwner || '',
                image: userBooking.image || [],
                inviteType: 'partecipant'
            });
        } else if (isGuestInvite(userBooking.inviteType) && Number(userBooking.uid) !== Number(organizerUid)) {
            invitedPlayers.push({
                uid: userBooking.uid,
                name: userBooking.partnerBookingName || '',
                surname: userBooking.partnerBookingSurname || '',
                nickname: userBooking.bookingOwner || '',
                image: userBooking.image || [],
                inviteType: 'guest'
            });
        } else if (isGuestInvite(userBooking.inviteType)) {
            invitedGuests.push({
                name: userBooking.partnerBookingName || '',
                surname: userBooking.partnerBookingSurname || '',
                email: userBooking.partnerBookingEmail === 'No e-mail' ? null : userBooking.partnerBookingEmail,
                phoneNumber: userBooking.partnerBookingPhoneNumber || ''
            });
        }
    });
    return {invitedPlayers, invitedGuests};
};

export const editableInvitationCapacity = (booking, slot, actualPlayers) => {
    const currentPlayers = Math.max(Number(actualPlayers || 0), 1);
    if (booking && booking.matchFormat === 'doubles') return 4;
    if (booking && booking.matchFormat === 'singles') return Math.max(2, Math.min(currentPlayers, 3));
    return Math.min(Number(slot.presetPlaces || currentPlayers), Math.max(Number(slot.minimumPlayers || 1), currentPlayers));
};

const PlayerBookingEditModal = ({
    isEditPlayersModalVisible,
    profile,
    setSelectedPlayer,
    startSlot,
    presetPlaces,
    renderPlayer,
    isPlayersModalVisible,
    partner,
    selectedPlayer,
    renderPlayerSearch,
    searchPlayers,
    clearPlayers,
    showPlayersModal,
    hidePlayersModal,
    showMessage,
    hideEditPlayersModal,
    saveEditedPlayers,
    booking
}) => {
    const {t} = useTranslation();
    const [insufficientCreditPlayer, setInsufficientCreditPlayer] = useState(null);
    const [showInsufficientCreditChoice, setShowInsufficientCreditChoice] = useState(false);
    const [showTopupConfirm, setShowTopupConfirm] = useState(false);
    const maximumPlayers = booking.matchFormat === 'doubles'
        ? 4
        : booking.matchFormat === 'singles'
        ? 3
        : presetPlaces;
    return(
        <Modal show={isEditPlayersModalVisible} onHide={hideEditPlayersModal}>
            <Modal.Header closeButton>
            <Modal.Title>{t('EDIT_PLAYERS')}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
            <Formik
            enableReinitialize
            initialValues={editableInvitationRoster(booking, profile.uid)}
            onSubmit={v => saveEditedPlayers(v.invitedPlayers, v.invitedGuests, startSlot.slotId, booking.bookingId)}
        >
            {({
            handleSubmit,
            handleChange,
            values,
            setFieldValue
            }) => {

            const actualPlayers = values.invitedPlayers.length + values.invitedGuests.length + 1;
            const billablePlayers = editableInvitationCapacity(booking, startSlot, actualPlayers);
    
            const addPlayerToBooking = (player, inviteType, topupInvitation = false) => {
                const invitedPlayer = {...player, inviteType};
                if (topupInvitation) invitedPlayer.topupInvitation = true;
                const alreadyInvited = values.invitedPlayers.some(p => p.uid === player.uid);
                if(!alreadyInvited) setFieldValue('invitedPlayers', [...values.invitedPlayers, invitedPlayer]);
                setSelectedPlayer({});
                setFieldValue('playerName', "");
                setFieldValue('playerSurname', "");
                setFieldValue('playerNickname', "");
                hidePlayersModal();
            };

            const addInvitedPlayer = (player, inviteType) => {
                if(inviteType === 'partecipant' && !player.hasSufficientFunds){
                setInsufficientCreditPlayer({...player});
                setShowInsufficientCreditChoice(true);
                hidePlayersModal();
                return;
                }
                addPlayerToBooking(player, inviteType);
            };

            const addInvitedGuest = (guest) => {
                var validEmailRegex = /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i;
                if(guest.email && !guest.email.match(validEmailRegex)) {
                showMessage(t('EMAIL'), t('INVALID_EMAIL'));
                return;
                }

                if(guest.email === ""){
                guest.email = null
                }

                if(!guest.surname){
                showMessage(t('MANDATORY_FIELDS'), t('INSERT_GUEST_NAME_SURNAME'));
                return;
                }

                if(!guest.name){
                guest.name = ""
                }

                setFieldValue('invitedGuests', [...values.invitedGuests, guest]);
                setFieldValue('friendName', "");
                setFieldValue('friendSurname', "");
                setFieldValue('friendEmail', null);
                setFieldValue('friendPhone', "");
                hidePlayersModal();
            };
        
            return(
                <Fragment>
                <Form onSubmit={handleSubmit}>
                    <Form.Group>
                    <Form.Label>{booking.matchFormat === 'doubles' ? t('PLAYERS_MIN_EQUALS_MAX', {minmax: 4}) : startSlot.minimumPlayers !== presetPlaces ? t('PLAYERS_MIN_MAX',{min: startSlot.minimumPlayers, max: maximumPlayers}) : t('PLAYERS_MIN_EQUALS_MAX',{minmax: presetPlaces})}</Form.Label>
                    {booking.matchFormat ? <Form.Text className="text-muted d-block mb-3">{booking.matchFormat === 'doubles' ? t('OPEN_MATCH_FORMAT_DOUBLE') : t('OPEN_MATCH_FORMAT_SINGLE')}</Form.Text> : null}
                    {renderPlayer(values.invitedPlayers, values.invitedGuests, setFieldValue, booking.matchFormat)}
                    {actualPlayers < billablePlayers ? <div className="skywork-booking-unassigned-notice"><strong>{t('SKYWORK_BOOK_NOW_WITH_UNASSIGNED_TITLE')}</strong><p>{t('SKYWORK_EDIT_UNASSIGNED_BODY', {count: billablePlayers - actualPlayers})}</p></div> : null}
                    {values.invitedPlayers.length + values.invitedGuests.length + 1 < maximumPlayers ?
                    <Button variant="secondary" style={{marginBottom: 50}} onClick={showPlayersModal}>{t('INVITE_PLAYER_NOT_REQUIRED', {n: values.invitedGuests.length + values.invitedPlayers.length + 2})}</Button>
                    : null}
                    {
                        <Form.Group>
                        <Button variant="primary" type="submit">{t('SAVE')}</Button>
                        </Form.Group>
                    }
                    </Form.Group>
                </Form>
                {isPlayersModalVisible ?
                <Modal show={isPlayersModalVisible} onHide={hidePlayersModal}>
                    <Modal.Header closeButton>
                    <Modal.Title>{t('BOOKING_PLAYERS_NUMBER_INDEX', {index: (values.invitedGuests || []).length + (values.invitedPlayers || []).length + 2})}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Label>{t('CHOOSE_INVITE_TYPE')}</Form.Label>
                        <Form.Check required name="inviteType" type="radio" label={t('INVITE_GUEST')} checked={values.inviteType === 'guest'} onChange={() => setFieldValue('inviteType', 'guest')}/>
                        <Form.Text className='text-muted' style={{paddingLeft: 20}}>{t('INVITE_GUEST_INFO')}</Form.Text>
                        <Form.Check required name="inviteType" type="radio" label={t('INVITE_PARTECIPANT')} checked={values.inviteType === 'partecipant'} onChange={() => setFieldValue('inviteType', 'partecipant')}/>
                        <Form.Text className='text-muted' style={{paddingLeft: 20, paddingBottom: 30}}>{t('INVITE_PARTECIPANT_INFO')}</Form.Text>
                        {values.inviteType === 'guest' ? 
                        <Fragment>
                        <Form.Label>{t('CHOOSE_GUEST')}</Form.Label>
                        <Form.Check required name="guestType" type="radio" label={t('PLAYER_GUEST')} checked={values.guestType === 'player'} onChange={() => setFieldValue('guestType', 'player')}/>
                        <Form.Text className='text-muted' style={{paddingLeft: 20}}>{t('PLAYER_GUEST_INFO')}</Form.Text>
                        <Form.Check required name="guestType" type="radio" label={t('FRIEND_GUEST')} checked={values.guestType === 'friend'} onChange={() => setFieldValue('guestType', 'friend')}/>
                        <Form.Text className='text-muted' style={{paddingLeft: 20, paddingBottom: 30}}>{t('FRIEND_GUEST_INFO')}</Form.Text>
                        </Fragment>
                        : null }
                        {values.inviteType === 'partecipant' || (values.inviteType === 'guest' && values.guestType === 'player') ?
                        <BookingPlayerSearch
                            partner={partner}
                            slotId={startSlot.slotId}
                            playersNumber={billablePlayers}
                            matchFormat={booking.matchFormat}
                            searchPlayers={searchPlayers}
                            clearPlayers={clearPlayers}
                            renderPlayer={renderPlayerSearch}
                            selectedPlayer={selectedPlayer}
                            onAddPlayer={() => addInvitedPlayer(selectedPlayer, values.inviteType)}
                            onGuestFallback={({name, surname}) => {
                                setSelectedPlayer({});
                                setFieldValue('inviteType', 'guest');
                                setFieldValue('guestType', 'friend');
                                setFieldValue('friendName', name);
                                setFieldValue('friendSurname', surname);
                            }}
                            showNickname
                        />
                        : values.guestType === 'friend' ?
                        <Fragment>
                        <Form.Group style={{display: 'flex', flexDirection: 'row'}} required>
                            <Form.Control required name="friendName" placeholder={t('NAME')} style={{marginRight: 20}} type="text" maxLength="25" minLength="2" value={values.friendName || ""} onChange={handleChange}/>
                            <Form.Control required name="friendSurname" placeholder={t('SURNAME')} type="text" maxLength="25" minLength="2" value={values.friendSurname || ""} onChange={handleChange}/>
                        </Form.Group>
                        <Form.Group style={{display: 'flex', flexDirection: 'row'}} required>
                            <Form.Control required name="friendEmail" placeholder={t('EMAIL')} style={{marginRight: 20}} type="text" maxLength="30" minLength="5" value={values.friendEmail || ""} onChange={handleChange}/>
                            <Form.Control name="friendPhone" className='no-arrow' placeholder={t('PHONE')} type="number" maxLength="10" minLength="10" value={values.friendPhone || ""} onChange={handleChange}/>
                        </Form.Group>
                        <Button type="button" style={{maxWidth: 100}} variant="secondary" onClick={() => addInvitedGuest({email: values.friendEmail, name: values.friendName, surname: values.friendSurname, phoneNumber: values.friendPhone})}>{t('SELECT')}</Button>
                        </Fragment>
                        : null}
                    </Form>
                    </Modal.Body>
                    <Modal.Footer>
                    <Button variant="primary" onClick={hidePlayersModal}>
                        {t('CLOSE_BUTTON')}
                    </Button>
                    </Modal.Footer>
                </Modal>
                : null}
                <Modal show={showInsufficientCreditChoice} onHide={() => setShowInsufficientCreditChoice(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('CANNOT_INVITE_PARTECIPANT')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="skywork-modal-body">
                        <p>{t('CANNOT_INVITE_PARTECIPANT_INFO', {
                            name: insufficientCreditPlayer ? insufficientCreditPlayer.name : '',
                            surname: insufficientCreditPlayer ? insufficientCreditPlayer.surname : ''
                        })}</p>
                        <div className="skywork-modal-actions-stacked">
                            <Button type="button" className="skywork-primary-action" onClick={() => {
                                setShowInsufficientCreditChoice(false);
                                setShowTopupConfirm(true);
                            }}>{t('SKYWORK_SEND_TOPUP_INVITE')}</Button>
                            <Button type="button" className="skywork-secondary-action" onClick={() => {
                                if (insufficientCreditPlayer) addInvitedPlayer(insufficientCreditPlayer, 'guest');
                                setShowInsufficientCreditChoice(false);
                                setInsufficientCreditPlayer(null);
                            }}>{t('SKYWORK_INVITE_AS_GUEST')}</Button>
                            <Button type="button" className="skywork-secondary-action" onClick={() => setShowInsufficientCreditChoice(false)}>{t('CLOSE_BUTTON')}</Button>
                        </div>
                    </Modal.Body>
                </Modal>
                <Modal show={showTopupConfirm} onHide={() => setShowTopupConfirm(false)} centered>
                    <Modal.Header closeButton>
                        <Modal.Title>{t('SKYWORK_TOPUP_CONFIRM_TITLE')}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="skywork-modal-body">
                        <p>{t('SKYWORK_TOPUP_CONFIRM_BODY', {name: insufficientCreditPlayer ? insufficientCreditPlayer.name : ''})}</p>
                        <ul className="skywork-topup-confirm-list">
                            <li>{t('SKYWORK_TOPUP_CONFIRM_EMAIL')}</li>
                            <li>{t('SKYWORK_TOPUP_CONFIRM_ACCEPT')}</li>
                            <li>{t('SKYWORK_TOPUP_CONFIRM_TRANSFER')}</li>
                            <li>{t('SKYWORK_TOPUP_CONFIRM_NOTIFY')}</li>
                        </ul>
                        <p className="skywork-topup-safety-note">{t('SKYWORK_TOPUP_CONFIRM_PENDING')}</p>
                        <div className="skywork-modal-actions-stacked">
                            <Button type="button" className="skywork-primary-action" onClick={() => {
                                if (insufficientCreditPlayer) addPlayerToBooking(insufficientCreditPlayer, 'guest', true);
                                setShowTopupConfirm(false);
                                setInsufficientCreditPlayer(null);
                            }}>{t('SKYWORK_TOPUP_CONFIRM_SEND')}</Button>
                            <Button type="button" className="skywork-secondary-action" onClick={() => setShowTopupConfirm(false)}>{t('CLOSE_BUTTON')}</Button>
                        </div>
                    </Modal.Body>
                </Modal>
                </Fragment>
            )}}
            </Formik>
            </Modal.Body>
            <Modal.Footer>
            <Button variant="primary" onClick={hideEditPlayersModal}>
                {t('CLOSE_BUTTON')}
            </Button>
            </Modal.Footer>
        </Modal>
    )
};

const mapStateToProps = ({login: {profile}, player: {isPlayersModalVisible, isEditPlayersModalVisible}}) => ({
    profile,
    isPlayersModalVisible,
    isEditPlayersModalVisible
  });
  
  const mapDispatchToProps = dispatch => ({
    showPlayersModal: () => {
      dispatch(showPlayersModal());
    },
    hidePlayersModal: () => {
      dispatch(hidePlayersModal());
      dispatch(cleanPlayersReceived());
    },
    searchPlayers: (playerNickname, playerName, playerSurname, playerAffiliate, slotId, partnerId, playersNumber, matchFormat) => {
      if (playerAffiliate === undefined) playerAffiliate = false;
      if (playerName) playerName = playerName.replace(/ /g,'_');
      if (playerSurname) playerSurname = playerSurname.replace(/ /g,'_');
      if (playerNickname) playerNickname = playerNickname.replace(/ /g,'_');
      const validRegex = /^[A-Za-z0-9_àèéìòù'-]*$/;
      if ((playerName && !playerName.match(validRegex)) || (playerSurname && !playerSurname.match(validRegex)) || (playerNickname && !playerNickname.match(validRegex))) {
        dispatch(showMessage('ACCEPTED_CHARACTERS', "ACCEPTED_CHARACTERS_INFO"));
        return Promise.resolve({status: 400, error: 'invalid_characters'});
      }
      return dispatch(searchPlayers(playerNickname, playerName, playerSurname, playerAffiliate, slotId, partnerId, playersNumber, matchFormat));
    },
    clearPlayers: () => dispatch(cleanPlayersReceived()),
    showMessage: (title, body) => {
      dispatch(showMessage(title,body));
    },
    hideEditPlayersModal: () => {
      dispatch(hideEditPlayersModal());
    },
    saveEditedPlayers: (invitedPlayers, invitedGuests, slotId, bookingId) => {
      dispatch(saveEditedPlayers(invitedPlayers, invitedGuests, slotId, bookingId));
    }
  });

export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
  )(PlayerBookingEditModal));
